import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import img28 from "../../../assets/img28.jpg";
import { CheckCircle, Plus, Minus } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function NoMedicalInsurance() {
  const container = "max-w-[1380px] mx-auto px-4 sm:px-6 md:px-10 lg:px-12";
  const [activeIndex, setActiveIndex] = useState(null);
  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "Comment fonctionne l'assurance vie sans examen médical?",
      answer:
        "L'assurance vie sans examen médical vous permet d'obtenir une couverture d'assurance sans passer d'examen médical traditionnel. Au lieu de fournir des analyses sanguines, des visites chez le médecin ou des dossiers de santé détaillés, vous pouvez remplir un bref questionnaire de santé ou répondre à quelques questions sur votre mode de vie. L'assureur souscrit votre demande en fonction de ces renseignements et de votre âge. Une fois approuvée, vous payez des primes et, si vous décédez pendant que la police est active, vos bénéficiaires reçoivent une prestation de décès libre d'impôt.",
    },
    {
      question: "De combien d'assurance vie sans examen médical ai-je besoin?",
      answer:
        "Pour déterminer le montant d'assurance vie sans examen médical dont vous avez besoin, tenez compte de vos responsabilités financières et de vos objectifs futurs. Calculez votre solde hypothécaire, vos dettes impayées, vos dépenses courantes, les coûts d'éducation de vos enfants et tout soutien financier dont votre famille pourrait avoir besoin. Comme les polices sans examen médical peuvent offrir des limites de couverture inférieures, vous pourriez envisager de la combiner avec une assurance traditionnelle pour des besoins plus importants. Un conseiller financier peut vous aider à estimer le montant approprié pour assurer la protection de vos proches.",
    },
    {
      question: "Comment puis-je acheter une assurance vie sans examen médical au Canada?",
      answer:
        "Pour acheter une assurance vie sans examen médical, commencez par comparer les assureurs qui offrent des polices sans examen médical au Canada. Vous pouvez demander des soumissions en ligne ou par l'intermédiaire d'un conseiller en assurance, en fournissant des renseignements de base comme l'âge, le mode de vie et le montant de couverture. Certains assureurs délivrent des approbations en quelques minutes puisqu'ils n'exigent pas d'examen médical en personne. Examinez attentivement les détails de la police, les primes et les modalités de couverture avant de choisir celle qui répond le mieux à vos besoins de protection financière.",
    },
    {
      question: "À quelle vitesse puis-je être approuvé pour une assurance vie sans examen médical?",
      answer:
        "L'approbation pour une assurance vie sans examen médical au Canada est généralement beaucoup plus rapide que pour les polices traditionnelles, car aucun examen médical en personne n'est requis. Plusieurs assureurs offrent une approbation instantanée ou le jour même pour les polices simples, tandis que d'autres peuvent prendre quelques jours pour examiner votre questionnaire de santé et votre demande. Ce délai rapide en fait une option idéale pour les personnes ayant besoin d'une couverture immédiate, les aînés, ou celles ayant des problèmes de santé préexistants qui pourraient faire face à des délais avec les polices d'assurance vie standards.",
    },
    {
      question: "Quel montant de couverture puis-je obtenir sans examen médical?",
      answer:
        "Les montants de couverture pour les polices d'assurance vie sans examen médical varient selon l'assureur et votre âge ou profil de santé. La plupart des assureurs offrent des polices allant de 25 000 $ à 500 000 $, adaptées à une protection financière à court ou moyen terme. Bien que la couverture soit généralement inférieure à celle des polices entièrement souscrites, vous pouvez souvent combiner l'assurance sans examen médical avec d'autres polices pour obtenir une protection plus élevée. Cela garantit que vos proches reçoivent un soutien suffisant pour couvrir les dettes, les frais funéraires ou d'autres dépenses essentielles.",
    },
  ];

  return (
    <>
      <SEO
        title="Assurance vie sans examen médical Canada | einsured.ca"
        description="Trouvez une assurance vie sans examen médical au Canada avec einsured.ca. Découvrez des options d’approbation rapide et une protection simplifiée."
        keywords="Assurance vie sans examen médical, assurance vie sans examen Canada, assurance vie à émission simplifiée"
      />
      {/* HERO */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:min-h-[80vh] lg:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start"
        style={{ backgroundImage: `url(${img28})` }}
      >
        <div className="absolute inset-0 bg-black/40" />

        <div className={`relative z-10 w-full ${container}`}>
          <div className="text-white max-w-2xl text-center md:text-left">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-3 sm:mb-4">
              Assurance vie sans examen médical
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-semibold mb-6 sm:mb-8">
              Obtenez jusqu'à 2 M$ de couverture sans examen médical
            </p>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full shadow-md transition"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* What Is No Medical Life Insurance */}
      <section className="py-12 sm:py-14 md:py-16 bg-white text-gray-800">
        <div className={container}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6 sm:mb-10">
              Qu'est-ce que l'assurance vie sans examen médical ?
            </h2>

            <p className="text-base sm:text-lg leading-8 mb-4 sm:mb-6">
              L'assurance vie sans examen médical, également connue sous le nom d'assurance
              vie sans test, offre la couverture vie dont vous avez besoin sans les examens
              médicaux traditionnels, souvent intrusifs.
            </p>

            <p className="text-base sm:text-lg leading-8 mb-4 sm:mb-6">
              Traditionnellement, souscrire une assurance vie impliquait de nombreuses
              questions sur la santé, des tests sanguins et des examens médicaux, ce qui
              pouvait retarder le processus d'approbation de plusieurs semaines, voire des
              mois. L'assurance vie sans examen médical élimine ces obstacles, vous offrant
              une voie efficace et simple pour avoir la tranquillité d'esprit.
            </p>

            <p className="text-base sm:text-lg leading-8">
              Au Canada, l'assurance vie sans examen médical connaît une popularité
              croissante, devenant rapidement la catégorie de produits d'assurance vie à la
              croissance la plus rapide. Ces polices offrent la commodité de ne pas exiger
              d'examen médical, de test sanguin ou d'évaluation des signes vitaux comme la
              tension artérielle, la taille et le poids. En raison de l'absence de ces tests,
              la couverture d'assurance vie peut être obtenue beaucoup plus rapidement,
              souvent immédiatement. Il n'est pas surprenant que ce type d'assurance soit
              très demandé.
            </p>
          </div>
        </div>
      </section>

      {/* Types of No Medical Life Insurance */}
      <section className="py-12 sm:py-14 md:py-6">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6 sm:mb-10">
              Types d'assurance vie sans examen médical
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-10">
              {/* Simplified Issue */}
              <div className="bg-white rounded-3xl shadow-md border border-gray-200 p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Émission simplifiée
                </h3>
                <p className="text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                  L'assurance vie à émission simplifiée ne nécessite pas d'examen médical,
                  mais peut impliquer de répondre à quelques questions relatives à la santé.
                  Elle est conçue pour la rapidité et la simplicité, offrant un processus de
                  demande simple et des délais de décision rapides.
                </p>
                <h4 className="text-lg sm:text-xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Caractéristiques :
                </h4>
                <ul className="space-y-3 sm:space-y-4 text-base sm:text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    Offre un processus de demande simple qui ne nécessite pas d'examens
                    médicaux, d'échantillons biologiques ou de souscription approfondie.
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    Aucune question sur les conditions médicales courantes et les habitudes
                    de vie.
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    Produits de haute qualité avec des caractéristiques attrayantes.
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    Processus de demande accessible et sans tracas avec vérification MIB.
                  </li>
                </ul>
              </div>

              {/* Guaranteed Issue */}
              <div className="bg-white rounded-3xl shadow-md border border-gray-200 p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Émission garantie
                </h3>
                <p className="text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                  Avec l'émission garantie, vous n'aurez pas à répondre à des questions sur
                  votre santé ni à passer un examen médical. Ce type est particulièrement
                  avantageux pour ceux qui ont des conditions de santé préexistantes qui les
                  rendraient normalement inadmissibles aux polices d'assurance vie
                  traditionnelles.
                </p>
                <h4 className="text-lg sm:text-xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Caractéristiques :
                </h4>
                <ul className="space-y-3 sm:space-y-4 text-base sm:text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    Aucun examen médical requis.
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    Acceptation immédiate.
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    La plupart des conditions préexistantes sont couvertes, mais il peut y
                    avoir une période d'attente (souvent 1 à 2 ans) avant que ces conditions
                    soient couvertes.
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    De nombreuses polices à émission garantie comportent des prestations de
                    décès progressives.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits of No Medical Life Insurance */}
      <section className="py-12 sm:py-14 md:py-16 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6 sm:mb-10">
              Avantages de l'assurance vie sans examen médical
            </h2>

            <ul className="space-y-6 sm:space-y-8 text-base sm:text-lg leading-relaxed max-w-4xl mx-auto">
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.2}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Processus de demande rapide et pratique :
                  </span>{" "}
                  Fini les processus de demande longs et fastidieux. Avec l'assurance vie
                  sans examen médical, vous pouvez faire une demande et obtenir des décisions
                  de couverture en quelques jours, voire en quelques heures, grâce à la
                  commodité des demandes numériques et des processus d'approbation simplifiés.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.2}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Idéal pour les personnes ayant des conditions préexistantes :
                  </span>{" "}
                  Pour beaucoup, l'assurance vie traditionnelle est hors de portée en raison
                  de leurs antécédents médicaux. L'assurance vie sans examen médical ouvre la
                  porte à davantage de personnes pour obtenir la couverture dont elles ont
                  besoin sans être pénalisées pour des conditions préexistantes.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.2}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Aucun test sanguin fastidieux requis :
                  </span>{" "}
                  L'assurance sans examen médical permet aux demandeurs d'obtenir une
                  assurance instantanée jusqu'à un certain montant sans subir d'examens
                  médicaux fastidieux ni d'analyse d'urine. Elle aide les demandeurs à éviter
                  les aiguilles et les procédures médicales épuisantes.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.2}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Flexibilité et tranquillité d'esprit :
                  </span>{" "}
                  Notre gamme de produits d'assurance vie sans examen médical offre une
                  flexibilité en termes de montants de couverture, de durées et de primes.
                  Cette flexibilité garantit que vous pouvez trouver une police qui répond à
                  vos besoins et vous procure la tranquillité d'esprit de savoir que vos
                  proches sont protégés.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Who Needs No Medical Insurance Coverage */}
      <section
        className="relative py-12 sm:py-14 md:py-16 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900/80" />
        <div className={`relative z-10 ${container}`}>
          <div className="max-w-7xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
              Qui a besoin d'une couverture d'assurance sans examen médical ?
            </h2>

            <p className="max-w-5xl mx-auto text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 text-gray-100">
              Dans le monde incertain d'aujourd'hui, assurer l'avenir de votre famille est
              plus important que jamais. Mais que se passe-t-il lorsque les polices
              d'assurance{" "}
              <span className="font-semibold">vie</span> traditionnelles semblent
              inaccessibles en raison d'examens médicaux que beaucoup trouvent intrusifs ou
              disqualifiants en raison de conditions préexistantes ? C'est là qu'intervient
              la couverture d'assurance sans examen médical — une solution inclusive
              garantissant à chacun la possibilité d'être protégé.
            </p>

            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#20a7df] mb-2 sm:mb-3">
              Aucun examen de santé, aucun souci !
            </h3>

            <p className="max-w-5xl mx-auto text-base sm:text-lg leading-relaxed mb-8 sm:mb-10 text-gray-100">
              Les polices d'assurance sans examen médical offrent une voie simple pour
              obtenir la couverture dont vous avez besoin sans avoir à passer d'examens
              médicaux.
            </p>

            <h4 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 sm:mb-10">
              Cela est particulièrement avantageux pour :
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 md:p-10 shadow-lg hover:shadow-xl transition text-gray-800">
                <h5 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Les personnes ayant des conditions préexistantes :
                </h5>
                <p className="text-base leading-relaxed text-gray-700">
                  Si vous avez des conditions de santé qui ont rendu difficile l'obtention
                  d'une assurance vie traditionnelle, l'assurance sans examen médical est
                  conçue pour vous.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 md:p-10 shadow-lg hover:shadow-xl transition text-gray-800">
                <h5 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Les personnes âgées :
                </h5>
                <p className="text-base leading-relaxed text-gray-700">
                  Au fil du temps, souscrire une assurance vie peut devenir plus complexe.
                  L'assurance sans examen médical offre un processus simplifié, garantissant
                  qu'il n'est jamais trop tard pour protéger vos proches.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 md:p-10 shadow-lg hover:shadow-xl transition text-gray-800">
                <h5 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Ceux qui valorisent leur vie privée :
                </h5>
                <p className="text-base leading-relaxed text-gray-700">
                  Si vous préférez ne pas divulguer vos antécédents médicaux ni passer des
                  examens de santé, nos options d'assurance sans examen médical respectent
                  votre vie privée tout en vous offrant la couverture dont vous avez besoin.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility Criteria */}
      <section className="py-12 sm:py-14 md:py-16 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6 sm:mb-10">
              Critères d'admissibilité à l'assurance vie sans examen médical
            </h2>

            <ul className="space-y-6 sm:space-y-8 text-base sm:text-lg leading-relaxed">
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.2}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Exigences d'âge :
                  </span>{" "}
                  Les polices d'assurance vie sans examen médical ont généralement des
                  exigences d'âge spécifiques pour les demandeurs. Celles-ci peuvent varier
                  selon la police et le fournisseur, mais visent généralement les adultes qui
                  peuvent trouver les polices traditionnelles moins accessibles en raison de
                  l'âge ou de préoccupations de santé.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.2}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Santé et antécédents médicaux :
                  </span>{" "}
                  Bien qu'un examen médical ne soit pas requis, les demandeurs pourraient
                  quand même avoir besoin de répondre à quelques questions liées à la santé.
                  L'admissibilité à l'assurance vie à émission simplifiée peut dépendre des
                  réponses à ces questions, tandis que l'assurance vie à émission garantie
                  n'a souvent aucune question sur la santé, la rendant plus accessible aux
                  personnes ayant des conditions préexistantes.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.2}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Besoins en couverture :
                  </span>{" "}
                  Les polices d'assurance vie sans examen médical ont souvent des limites de
                  couverture inférieures à celles des polices d'assurance vie traditionnelles.
                  En tant que telles, elles conviennent mieux aux personnes cherchant à
                  couvrir des dépenses spécifiques comme les frais funéraires ou les dettes
                  en cours plutôt que de fournir un soutien financier à long terme pour les
                  bénéficiaires.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Why Choose einsured.ca */}
      <section className="py-12 sm:py-14 md:py-6 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6 sm:mb-8">
              Pourquoi choisir einsured.ca pour l'assurance vie sans examen médical ?
            </h2>

            <p className="text-base sm:text-lg leading-8 mb-8 sm:mb-10 text-center max-w-5xl mx-auto">
              Chez{" "}
              <a href="/" className="text-sky-600 underline hover:opacity-90">
                einsured.ca
              </a>
              , nous comprenons l'importance de trouver une solution d'assurance qui
              correspond à vos besoins, à votre style de vie et à votre budget. Voici
              pourquoi nous choisir pour vos besoins en assurance vie sans examen médical
              est judicieux :
            </p>

            <ul className="space-y-6 sm:space-y-8 text-base sm:text-lg leading-relaxed max-w-5xl mx-auto">
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.2}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Faites une demande en ligne en 20 minutes :
                  </span>{" "}
                  Profitez de la commodité d'explorer, de demander et de gérer votre police
                  d'assurance vie sans examen médical en ligne, depuis le confort de votre
                  domicile.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.2}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Solutions personnalisées :
                  </span>{" "}
                  Notre plateforme numérique donne accès à une gamme d'options d'assurance
                  vie sans examen médical, vous assurant de trouver une police qui répond à
                  vos exigences spécifiques.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.2}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Soutien d'experts :
                  </span>{" "}
                  Notre équipe d'experts en assurance est toujours prête à vous guider tout
                  au long du processus, à répondre à vos questions et à vous fournir des
                  conseils personnalisés pour vous aider à prendre des décisions éclairées.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.2}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Tarifs compétitifs :
                  </span>{" "}
                  Nous nous engageons à offrir des tarifs compétitifs et des options de
                  paiement flexibles, rendant une couverture d'assurance vie de qualité
                  accessible à tous.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Revolutionizing Insurance */}
      <section className="relative bg-[#e7edf4] border-t-4 border-b-4 border-[#2b4672] py-12 sm:py-14 md:py-16">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-center text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 sm:mb-6">
              Révolutionner l'assurance pour tous : même quand les autres disent non !
            </h2>

            <p className="text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
              Reconnaissant les défis auxquels beaucoup font face pour obtenir une couverture
              en raison de refus, de modifications ou de reports par d'autres assureurs, nous
              cherchons à protéger presque tout le monde grâce à divers produits d'assurance.
            </p>

            <p className="text-base sm:text-lg leading-relaxed mb-6 sm:mb-10">
              En acceptant les cas qui ont été refusés, cotés, modifiés ou reportés, nous
              nous efforçons de fournir une solution d'assurance équitable pour vous protéger,
              vous et vos proches. Notre approche est ancrée dans la compréhension et la prise
              en compte de vos préoccupations de santé tout en offrant l'avantage de primes
              basses, sans compromettre la qualité de la couverture.
            </p>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-6 sm:mb-8">
              Conclusion
            </h3>

            <p className="text-base sm:text-lg leading-relaxed mb-4 sm:mb-6 text-gray-800">
              <span className="font-semibold text-[#49628b]">
                L'assurance vie sans examen médical
              </span>{" "}
              offre une voie pratique et accessible pour sécuriser l'avenir de vos proches
              sans les tracas des examens médicaux. Que vous cherchiez une assurance vie à
              émission simplifiée ou à émission garantie,{" "}
              <span className="font-semibold text-[#49628b]">einsured.ca</span>{" "}
              est là pour vous offrir des solutions numériques adaptées à votre budget et à
              votre style de vie. Commencez à explorer vos options d'assurance vie sans
              examen médical dès aujourd'hui et faites le premier pas vers la tranquillité
              d'esprit et la sécurité financière. Une analyse appropriée des besoins sera
              effectuée avant de recommander un produit d'assurance vie.
            </p>

            <p className="text-base sm:text-lg leading-relaxed mb-4 sm:mb-6 text-gray-800">
              <span className="font-semibold text-[#49628b]">einsured.ca</span>{" "}
              s'associe à vous pour sécuriser un avenir plus lumineux et plus sûr. Si vous
              avez été refusé par d'autres assureurs, laissez-nous vous montrer la
              différence. Avec notre accent sur les préoccupations de santé, notre engagement
              envers des primes basses et notre approche inclusive, nous sommes là pour
              garantir que tout le monde a accès à la protection dont il a besoin.
            </p>

            <p className="text-base sm:text-lg leading-relaxed mb-8 sm:mb-10 text-gray-800">
              Découvrez un monde de possibilités d'assurance où vos besoins sont prioritaires.
              Contactez-nous dès aujourd'hui pour commencer votre parcours vers une solution
              d'assurance complète et abordable, taillée sur mesure pour vous.
            </p>

            <div className="flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-10 py-4 rounded-full shadow-md transition duration-200"
              >
                Réservez une réunion maintenant !
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b]">
              Foire aux questions sur l'assurance vie sans examen médical
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => {
              const expanded = activeIndex === index;
              const panelId = `faq-panel-${index}`;
              const btnId = `faq-btn-${index}`;

              return (
                <div
                  key={index}
                  className="border rounded-lg shadow-sm transition-all bg-white"
                >
                  <button
                    id={btnId}
                    className="w-full flex justify-between items-center text-left px-4 sm:px-6 py-3.5 sm:py-4 font-semibold text-base sm:text-lg text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e48a0] focus-visible:ring-offset-2"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                  >
                    <span>{faq.question}</span>

                    {expanded ? (
                      <Minus
                        className="w-6 h-6 text-gray-700 shrink-0 ml-4"
                        aria-hidden="true"
                      />
                    ) : (
                      <Plus
                        className="w-6 h-6 text-gray-700 shrink-0 ml-4"
                        aria-hidden="true"
                      />
                    )}
                  </button>

                  {expanded && (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={btnId}
                      className="px-4 sm:px-6 pb-4 text-gray-700 text-sm sm:text-base leading-relaxed"
                    >
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
}
